import React from "react";
import { Plus, Sparkles, BookOpen } from "lucide-react";

/**
 * First-run empty state — rendered by Schedule.jsx when the signed-in user has
 * no projects yet. Two clear next steps: create a real project, or load the
 * Riverside Festival demo to learn the tool.
 */
export default function EmptyState({ user, onCreate, onLoadDemo, onOpenHelp }) {
  return (
    <div
      className="flex-1 flex items-center justify-center px-6 bg-bg-base"
      data-testid="empty-state"
    >
      <div className="w-[520px] max-w-full">
        <div className="mono text-2xs uppercase tracking-[0.3em] text-amber mb-3">
          PRODUCTION SCHEDULE · WELCOME
        </div>
        <h1 className="text-3xl font-light text-ink leading-tight">
          {user?.name ? `Hi, ${user.name.split(" ")[0]}.` : "Hi."}
        </h1>
        <p className="text-sm text-ink-dim mt-3 leading-relaxed">
          You don't have any projects yet. Create one for your festival or event, or load
          the Riverside Festival demo to see how the tool works.
        </p>

        <div className="mt-6 space-y-2">
          <button
            type="button"
            onClick={onCreate}
            className="w-full flex items-center gap-3 px-4 py-3 bg-amber text-bg-base hover:bg-amber-light transition-colors group"
            data-testid="empty-create-btn"
          >
            <Plus className="w-4 h-4 shrink-0" />
            <div className="text-left flex-1">
              <div className="mono text-2xs uppercase tracking-widest">CREATE NEW PROJECT</div>
              <div className="text-xs opacity-80 mt-0.5">Empty grid. Add rooms, crew, and tasks.</div>
            </div>
          </button>

          <button
            type="button"
            onClick={onLoadDemo}
            className="w-full flex items-center gap-3 px-4 py-3 border border-line hover:border-amber hover:bg-amber-glow transition-colors group"
            data-testid="empty-load-demo-btn"
          >
            <Sparkles className="w-4 h-4 shrink-0 text-violet" />
            <div className="text-left flex-1">
              <div className="mono text-2xs uppercase tracking-widest text-ink">LOAD RIVERSIDE DEMO</div>
              <div className="text-xs text-ink-mute mt-0.5">A 4-day festival build with 6 stages and 60+ tasks.</div>
            </div>
          </button>

          <button
            type="button"
            onClick={onOpenHelp}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-elevated transition-colors text-ink-dim hover:text-ink"
            data-testid="empty-open-help-btn"
          >
            <BookOpen className="w-4 h-4 shrink-0" />
            <div className="text-left flex-1">
              <div className="mono text-2xs uppercase tracking-widest">READ THE DOCS</div>
              <div className="text-xs text-ink-mute mt-0.5">Six articles. Five minutes to fluency.</div>
            </div>
          </button>
        </div>

        <div className="mt-6 mono text-2xs uppercase tracking-widest text-ink-mute">
          Signed in as {user?.email || "—"}
        </div>
      </div>
    </div>
  );
}
